import { useState, useRef } from 'react';
import { OperationVariables } from '@apollo/client';
import { Grid, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useReactToPrint } from 'react-to-print';
import LoadingScreen from '@lwtears/lwt-common-frontend/lib/LoadingScreen';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import useDomainStore from 'graph/state/query-hooks/use-domains';
import OutlineButton from 'components/common/Buttons/OutlineButton';
import AssessmentBarCharts from './AssessmentBarCharts';
import { AssessmentType, BenchmarkAssessment, BenchmarkPeriod, Student } from 'types';
import useAssessmentReportsStore from 'graph/state/query-hooks/use-assessment-reports';
import useChecklistsStore from 'graph/state/query-hooks/use-checklists';
import { NotepadSrc } from 'components/common/NotepadSrc';
import PrintableClassReport from './PrintableClassReport';
import { getCurriculumBenchmarkAssessment } from 'util/assessments-util';

const t = (key) => translate('ui', key);

const BenchmarkReports = (props: {
  students: Student[];
  curriculumId: number;
  classroomId: number;
}) => {
  const [selectedBenchmark, setBenchmark] = useState('all');
  const [selectedDomain, setDomain] = useState('total');

  const benchmarkOrder = Object.values(BenchmarkPeriod);
  const classes = useStyles();

  const [domainStore] = useDomainStore({
    where: { productCurriculumId: props.curriculumId },
  } as OperationVariables);
  const studentIds = props.students.map((student) => {
    return student.id;
  });
  const [assessmentReportStore] = useAssessmentReportsStore({
    where: {
      studentId: studentIds,
      assessmentType: AssessmentType.Benchmark,
      productCurriculumId: props.curriculumId,
    },
  } as OperationVariables);
  const [checklistsStore] = useChecklistsStore({
    where: {
      productCurriculumId: props.curriculumId,
      type: AssessmentType.Benchmark,
    },
  } as OperationVariables);

  if (
    assessmentReportStore === undefined ||
    domainStore === undefined ||
    checklistsStore === undefined
  ) {
    return <LoadingScreen backgroundImage={NotepadSrc} />;
  }

  const reportExists = assessmentReportStore.data?.filter((v) =>
    (v.assessment as BenchmarkAssessment).details.some(
      (x) => x.productCurriculumId === props.curriculumId
    )
  );

  const sortedReports =
    assessmentReportStore && assessmentReportStore.data
      ? assessmentReportStore.data.sort((a, b) => {
          const acba = getCurriculumBenchmarkAssessment(
            a.assessment as BenchmarkAssessment,
            props.curriculumId
          );

          const bcba = getCurriculumBenchmarkAssessment(
            b.assessment as BenchmarkAssessment,
            props.curriculumId
          );

          return benchmarkOrder.indexOf(acba.period) - benchmarkOrder.indexOf(bcba.period);
        })
      : [];

  const reference = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => reference.current,
  });

  return (
    <>
      <div className={classes.mainContainer} style={{ flexGrow: 1 }}>
        <div style={{ display: 'none' }}>
          <PrintableClassReport
            ref={reference}
            reports={sortedReports}
            benchmark={selectedBenchmark}
            domainId={selectedDomain}
            allDomains={domainStore.domains || []}
            students={props.students}
            classroomId={props.classroomId}
            productCurriculumId={props.curriculumId}
          />
        </div>
      </div>
      <div className={classes.flexView}>
        <Grid container item xs={12}>
          <Grid container item xs={6} spacing={2}>
            <Grid item xs={5}>
              <Typography id="benchmark-select-label" variant="subtitle1">
                {t('report.changeBenchmark')}
              </Typography>
              <FormControl style={{ width: '100%' }}>
                <Select
                  labelId="benchmark-select-label"
                  value={selectedBenchmark}
                  onChange={(e) => setBenchmark(e.target.value as string)}
                >
                  <MenuItem value="all">{t('report.benchmarkAll')}</MenuItem>
                  <MenuItem value={BenchmarkPeriod.Beginning}>
                    {t('report.benchmarkPeriod-BEGINNING')}
                  </MenuItem>
                  <MenuItem value={BenchmarkPeriod.Middle}>
                    {t('report.benchmarkPeriod-MIDDLE')}
                  </MenuItem>
                  <MenuItem value={BenchmarkPeriod.End}>{t('report.benchmarkPeriod-END')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={7}>
              <Typography id="domain-select-label" variant="subtitle1">
                {t('report.changeDomain')}
              </Typography>
              <FormControl style={{ width: '100%' }}>
                <Select
                  labelId="domain-select-label"
                  value={selectedDomain}
                  onChange={(e) => setDomain(e.target.value as string)}
                >
                  <MenuItem value="total">{t('report.domainTotal')}</MenuItem>
                  {domainStore?.domains?.map((domain) => {
                    return (
                      <MenuItem key={'domain-' + domain.id} value={domain.id}>
                        {domain.title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container item xs={6} direction="row-reverse" className={classes.halfHeight}>
            <OutlineButton onClick={handlePrint}>{t('assessments.print')}</OutlineButton>
          </Grid>
        </Grid>
      </div>
      <div className={classes.flexCenter}>
        {reportExists &&
          checklistsStore.checklists &&
          domainStore.domains &&
          (selectedBenchmark === 'all' ? (
            <AssessmentBarCharts
              reports={sortedReports}
              benchmark={selectedBenchmark}
              domain={selectedDomain}
              allDomains={domainStore.domains}
              checklists={checklistsStore.checklists}
              students={props.students}
              classroomId={props.classroomId}
              curriculumId={props.curriculumId}
              setStateCallback={setBenchmark}
            />
          ) : (
            <AssessmentBarCharts
              reports={sortedReports}
              benchmark={selectedBenchmark}
              domain={selectedDomain}
              allDomains={domainStore.domains}
              checklists={checklistsStore.checklists}
              students={props.students}
              classroomId={props.classroomId}
              curriculumId={props.curriculumId}
              setStateCallback={setDomain}
            />
          ))}
      </div>
    </>
  );
};

export default BenchmarkReports;

const useStyles = makeStyles((theme: Theme) => {
  return {
    flexView: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(4),
      '& .MuiFormLabel-root': {
        fontSize: '1.2rem',
      },
    },
    flexCenter: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    mainContainer: {
      paddingLeft: theme.spacing(30),
      paddingRight: theme.spacing(30),
      paddingBottom: theme.spacing(10),
    },
    halfHeight: {
      height: '50%',
    },
  };
});
